import React, { useCallback, useState } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
  Box,
  createStyles,
  makeStyles,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import {
  DatePicker,
  KeyboardTimePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';
import { toast } from 'react-toastify';
import { daysOfWeek, IDaysOfWeek } from '../../../../utils/constants';
import { useCreateSchedule } from '../../../../hooks/data/schedules';
import {
  ISchedule,
  IScheduleDays,
} from '../../../../shared/interfaces/schedule-interfaces';
import LoadingButton from '../../../../components/LoadingButton';

type IProps = {
  open: boolean;
  handleClose: () => void;
  isNewBlockedSchedule: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      borderRadius: '50%',
      minWidth: '0',
      width: '30px',
      height: '30px',
      margin: 'auto',
    },
    timeField: {
      marginTop: theme.spacing(2),
    },
  }),
);

const initialDaysState = Object.keys(daysOfWeek).reduce((acc, day) => {
  return {
    ...acc,
    [day]: false,
  };
}, {} as IScheduleDays);

const initialFormData: ISchedule = {
  ...initialDaysState,
  label: '',
  start_time: moment(moment.now()).toDate(),
  end_time: moment(moment.now()).toDate(),
  start_date: moment(moment.now()).toDate(),
  end_date: moment(moment.now()).toDate(),
  available: false,
};

const ScheduleForm: React.FC<IProps> = ({
  open,
  handleClose,
  isNewBlockedSchedule,
}) => {
  const classes = useStyles();
  const [data, setData] = useState({
    ...initialFormData,
    available: isNewBlockedSchedule,
  });

  const createSchedule = useCreateSchedule();

  const handleDateChange = useCallback(
    (name: string) => (date: Date | null) => {
      setData({
        ...data,
        [name]: date,
      });
    },
    [data],
  );

  const handleSubmit = useCallback(async () => {
    try {
      const formData = {
        ...data,
        available: !isNewBlockedSchedule,
        start_date: moment(data.start_date).format('DD-MM-yyyy'),
        end_date: moment(data.end_date).format('DD-MM-yyyy'),
        start_time: moment(data.start_time).format('HH:mm'),
        end_time: moment(data.end_time).format('HH:mm'),
      };

      await createSchedule.mutateAsync(formData);

      setData(initialFormData);
      handleClose();
    } catch (err) {
      toast.error('Erro no cadastro de um horário');
    }
  }, [data, isNewBlockedSchedule, createSchedule, handleClose]);

  const handleToggleDay = useCallback(
    (day: keyof IScheduleDays) => () => {
      setData({
        ...data,
        [day]: !data[day],
      });
    },
    [data],
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        {isNewBlockedSchedule
          ? 'Cadastro de Restrição de Horário'
          : 'Cadastro de Horário'}
      </DialogTitle>
      <DialogContent>
        <Box width="100%" display="flex" flexDirection="column">
          <TextField
            variant="outlined"
            name="label"
            type="text"
            label="Etiqueta"
            value={data.label}
            margin="normal"
            onChange={e =>
              setData({
                ...data,
                label: e.target.value,
                // eslint-disable-next-line prettier/prettier
              })}
          />
          <Box mt={2} mb={2} display="flex" maxWidth="400px" width="100%">
            {Object.keys(daysOfWeek).map(day => (
              <Button
                key={day}
                className={classes.button}
                onClick={handleToggleDay(day as keyof IDaysOfWeek)}
                variant="contained"
                color="primary"
                style={
                  !data[day as keyof IDaysOfWeek]
                    ? {
                        background: 'transparent',
                      }
                    : {}
                }
              >
                {daysOfWeek[day as keyof IDaysOfWeek][0]}
              </Button>
            ))}
          </Box>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              disablePast
              margin="normal"
              format="dd/MM/yyyy"
              label="Data de início"
              value={data.start_date}
              onChange={handleDateChange('start_date')}
            />
            <DatePicker
              disablePast
              margin="normal"
              format="dd/MM/yyyy"
              label="Data de Fim"
              value={data.end_date}
              onChange={handleDateChange('end_date')}
            />
            <KeyboardTimePicker
              ampm={false}
              margin="normal"
              id="startTime"
              label="Horário de Início"
              value={data.start_time}
              onChange={handleDateChange('start_time')}
              KeyboardButtonProps={{
                'aria-label': 'change time',
              }}
            />
            <KeyboardTimePicker
              ampm={false}
              margin="normal"
              id="endTime"
              label="Horário de Término"
              value={data.end_time}
              onChange={handleDateChange('end_time')}
              KeyboardButtonProps={{
                'aria-label': 'change time',
              }}
            />
          </MuiPickersUtilsProvider>
        </Box>
        {createSchedule.status === 'error' && (
          <Typography color="error">
            {createSchedule.error?.response?.data?.non_field_errors.join(', ')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="primary"
          disabled={createSchedule.isLoading}
        >
          Cancelar
        </Button>
        <LoadingButton
          loading={createSchedule.isLoading}
          onClick={handleSubmit}
          color="primary"
          variant="contained"
        >
          Salvar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export { ScheduleForm };
